import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, GitBranch, Send, AlertCircle, Loader2, CheckCircle2, Upload } from 'lucide-react';
import { haptic } from '../../telegram';
import { marketplaceApi } from '../../api/marketplaceApi';
import { pipelinesApi, type PipelineInstance } from '../../api/pipelinesApi';
import { agentsApi } from '../../api/agentsApi';
import type { Agent } from '../../types/contracts';

type Source = { id: string; name: string; kind: 'agent' | 'pipeline' };

export function MarketplacePublish() {
  const nav = useNavigate();
  const [sources, setSources] = useState<Source[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('general');
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  useEffect(() => {
    Promise.all([
      pipelinesApi.list().catch(() => [] as PipelineInstance[]),
      agentsApi.list().catch(() => [] as Agent[]),
    ]).then(([pipes, ags]) => {
      const list: Source[] = [
        ...(pipes || []).map((p) => ({ id: p.id, name: p.name, kind: 'pipeline' as const })),
        ...(ags || []).map((a) => ({ id: a.id, name: a.name, kind: 'agent' as const })),
      ];
      setSources(list); setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const selected = sources.find((s) => s.id === selectedId);

  const publish = async () => {
    if (!selected || !title.trim()) return;
    haptic('medium'); setPublishing(true); setResult(null);
    try {
      let manifest: any;
      if (selected.kind === 'agent') { const r = await marketplaceApi.exportAgentManifest(selected.id); manifest = r.manifest; }
      else { const r = await marketplaceApi.exportPipelineManifest(selected.id); manifest = r.manifest; }
      const listing = await marketplaceApi.createListing({ title: title.trim(), summary: summary.trim() || title.trim(), category, kind: selected.kind, manifest });
      await marketplaceApi.submitListing(listing.id);
      setResult({ ok: true, msg: 'Отправлено на модерацию. Решение придёт в Telegram.' });
      haptic('heavy');
    } catch (e: any) {
      setResult({ ok: false, msg: e?.message || 'Ошибка публикации' });
    } finally { setPublishing(false); }
  };

  return (
    <div className="rf-detail">
      <div className="rf-topbar"><button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button><div className="rf-topbar-title">Опубликовать</div></div>

      <div className="rf-detail-scroll">
        <div className="rf-info"><Upload /><div><b>Публикация в маркет</b><br />Выберите свой сценарий или агента для публикации. После модерации листинг станет доступен всем.</div></div>

        {loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : sources.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><AlertCircle /></div><h3>Нет данных</h3><p>Сначала создайте сценарий или агента</p></div>
        ) : (
          <>
            <div className="rf-seclabel">Что публикуем?</div>
            <div className="rf-rows">
              {sources.map((s) => (
                <button key={s.id} className={'rf-srcrow' + (selectedId === s.id ? ' selected' : '')} onClick={() => { haptic('light'); setSelectedId(s.id); if (!title) setTitle(s.name); }}>
                  <div className="rf-srcrow-ic">{s.kind === 'agent' ? <Bot /> : <GitBranch />}</div>
                  <div className="rf-srcrow-body"><div className="rf-srcrow-name">{s.name}</div><div className="rf-srcrow-sub">{s.kind === 'agent' ? 'Агент' : 'Сценарий'}</div></div>
                  <div className={'rf-radio' + (selectedId === s.id ? ' on' : '')} />
                </button>
              ))}
            </div>

            {selected && (
              <div className="rf-mt18">
                <div className="rf-field"><label>Название</label><input className="rf-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Название в каталоге" /></div>
                <div className="rf-field"><label>Краткое описание</label><textarea className="rf-textarea" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Что делает, для кого" /></div>
                <div className="rf-field"><label>Категория</label>
                  <select className="rf-select" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="general">Общее</option>
                    <option value="support">Поддержка</option>
                    <option value="content">Контент</option>
                    <option value="productivity">Продуктивность</option>
                    <option value="automation">Автоматизация</option>
                    <option value="fun">Развлечения</option>
                  </select>
                </div>
                <button className="rf-btn primary block" onClick={publish} disabled={!title.trim() || publishing}>{publishing ? <Loader2 className="spin" /> : <Send />}{publishing ? 'Публикация…' : 'Отправить на модерацию'}</button>
              </div>
            )}

            {result && (
              <div className={'rf-result ' + (result.ok ? 'ok' : 'err')}>{result.ok ? <CheckCircle2 /> : <AlertCircle />}<div>{result.msg}</div></div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
